import * as stylex from "@stylexjs/stylex";
import { copy } from "@/copy";
import { eventDayKey } from "@/domain/allDay";
import type { CalendarEvent, Tag } from "@/domain/schemas";
import { whoLabel } from "@/domain/tags";
import { type Clock, clockAt, formatMonthDay, formatWeekday } from "@/domain/time";
import { addDays, dateKey, parseDateKey } from "@/domain/zone";
import "@/theme/tokens.stylex";
import { colors, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Eyebrow } from "@/ui/Eyebrow";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
type ComingUpProps = {
  events: readonly CalendarEvent[];
  tags: readonly Tag[];
  now: Date;
  clock: Clock;
  tz: string;
  small: boolean;
  onOpenEvent: (event: CalendarEvent) => void;
};
type RowProps = Omit<ComingUpProps, "events" | "small"> & {
  event: CalendarEvent;
};
const MAX_DOTS = 4;
const DAY_COLUMN = {
  small: 112,
  wide: 128
} as const;
const _temp = {
  k1xSpc: "xrvj5dj",
  kumcoG: "xqketvx",
  k1C7PZ: "x1cxx1dg",
  kGNEyG: "x7a106z",
  k8WAf4: "xxlmvz2",
  kg3NbH: "xnjsko4",
  kB7OPa: "x9f619",
  kMzoRj: "xc342km",
  kWkggS: "xjbqb8w",
  kVVagm: "xln7xf2",
  kMwMTN: "x1a11ngh",
  k9WMMc: "x1yc453h",
  kkrTdU: "x1ypdohk",
  kGVxlE: "x1jidhvw " + "xdjqlec",
  "$$css": true
};
const styles = {
  row: (column: number) => [_temp, {
    "--x-gridTemplateColumns": `${column}px minmax(0, 1fr)` != null ? `${column}px minmax(0, 1fr)` : undefined
  }]
};
export function ComingUp({
  events,
  small,
  ...row
}: ComingUpProps) {
  return <div className="x78zum5 xdt5ytf xt4j9g6 xu2p5k7 x96kwfj xeuugli">
      <Eyebrow>{copy.today.schedule.comingUp}</Eyebrow>
      <div className="x78zum5 xdt5ytf xr5lh1o">
        {events.map(event => <ComingUpRow key={event.id} event={event} column={DAY_COLUMN[small ? "small" : "wide"]} {...row} />)}
      </div>
    </div>;
}
function ComingUpRow({
  event,
  tags,
  now,
  clock,
  tz,
  column,
  onOpenEvent
}: RowProps & {
  column: number;
}) {
  const day = dayLabel(event, now, tz);
  const time = event.allDay ? copy.today.schedule.allDay : clockAt(event.startAt, clock, tz);
  const who = whoLabel(event, tags, copy.events.everyone);
  const dots = dotsOf(event, tags);
  return <button type="button" aria-label={copy.today.schedule.upcomingName(event.title, day, time, who)} onClick={() => onOpenEvent(event)} {...stylex.props(styles.row(column), press.row)}>
      <span className="x78zum5 xdt5ytf x195vfkc xr0iwhn xcheg63 xss6m8b xuxw1ft xtvhhri">
        <span className="xif65rj xdod15v">{day}</span>
        <span className="x4z9k3i xd4r4e8">{time}</span>
      </span>
      <span className="x78zum5 xdt5ytf xt4j9g6 xeuugli">
        <span className="xosj86m xfbjfs5 x1yrajzq x132q4wb x1fzhlzt">{event.title}</span>
        <span className="x78zum5 x6s0dn4 x152posx xeuugli x1jvydc1 x15i68ds xwn7fz2">
          {dots.length > 0 && <span className="x78zum5 x1ed6fcf x2lah0s">
              {dots.map(tag => <PersonDot key={tag.id} color={tag.color} />)}
            </span>}
          <span className="xuxw1ft xb3r6kr xlyipyv xeuugli">
            {[who, event.location].filter(Boolean).join(" · ")}
          </span>
        </span>
      </span>
    </button>;
}
function dayLabel(event: CalendarEvent, now: Date, tz: string): string {
  const key = eventDayKey(event, tz);
  if (key === addDays(dateKey(now, tz), 1)) return copy.today.schedule.tomorrow;
  const date = parseDateKey(key);
  return copy.today.schedule.day(formatWeekday(date, "UTC"), formatMonthDay(date, "UTC"));
}
function dotsOf(event: CalendarEvent, tags: readonly Tag[]): Tag[] {
  if (event.everyone) return [];
  const on = tags.filter(tag => event.tagIds.includes(tag.id));
  return on.length < tags.length ? on.slice(0, MAX_DOTS) : [];
}